import React, { useMemo } from 'react';

import Grid from '@material-ui/core/Grid';

import { UiFilterSelect } from '../../ui/ui-filter-select';
import { UiFilterTextField } from '../../ui/ui-filter-text-field';
import { UiFilterToggle } from '../../ui/ui-filter-toggle';

import { StatusChangeReasonFilter } from './status-change-reason-filter';

import {
  getAllHiringStatuses,
  getAllStatuses,
  SAVED_STATUS,
  statusDisplay,
} from '../../config/application-status-config';
import { formatLocation } from '../../util/formatters';
import { sortAlphabetically, SORT_ORDER } from '../../util/sort-functions';

export type FilterOnChangeHandler = (searchValue: string) => void;

type InputProps = {
  variant: 'small' | 'large';
  jobs: Job[];
  orgs: Org[];
  statusChangeReasons: { [reason: string]: number[] };
  filterValues: {
    filterByNameOrPhoneNumber?: string;
    filterByOrgId?: string;
    filterByJobId?: string;
    filterByLocation?: string;
    filterByStatus?: ApplicationStatus | '';
    filterByReason?: string;
    includeRejectedExpired?: boolean;
  };
  filterHandlers: {
    updateSearchTerm: FilterOnChangeHandler;
    handleFilterByOrgIdChange: FilterOnChangeHandler;
    handleFilterByJobIdChange: FilterOnChangeHandler;
    handleFilterByLocationChange: FilterOnChangeHandler;
    handleFilterByStatusChange: FilterOnChangeHandler;
    handleFilterByReasonChange: FilterOnChangeHandler;
    handleIncludeRejectedExpiredChange: () => void;
  };
  fieldsDisabled?: boolean;
  filterWidth: string;
  buttonWidth?: string;
};

const defaultFilterValues = {
  defaultFilterByName: '',
  defaultFilterByOrgId: '',
  defaultFilterByJobId: '',
  defaultFilterByLocation: '',
  defaultFilterByStatus: '',
  defaultFilterByReason: '',
  defaultIncludeRejectedExpired: false,
};

export const ApplicantFilters = ({
  jobs,
  orgs,
  statusChangeReasons,
  filterValues,
  filterHandlers,
  fieldsDisabled,
  filterWidth,
  buttonWidth,
  variant,
}: InputProps) => {
  const {
    filterByNameOrPhoneNumber,
    filterByStatus,
    filterByOrgId,
    filterByJobId,
    filterByLocation,
    filterByReason,
    includeRejectedExpired,
  } = filterValues;
  const {
    defaultFilterByName,
    defaultFilterByStatus,
    defaultFilterByOrgId,
    defaultFilterByJobId,
    defaultFilterByLocation,
    defaultFilterByReason,
    defaultIncludeRejectedExpired,
  } = defaultFilterValues;
  const {
    updateSearchTerm,
    handleFilterByOrgIdChange,
    handleFilterByJobIdChange,
    handleFilterByLocationChange,
    handleFilterByStatusChange,
    handleFilterByReasonChange,
    handleIncludeRejectedExpiredChange,
  } = filterHandlers;

  const orgOptions = (orgs || [])
    .map((org) => ({
      label: org.name,
      value: org.org_id.toString(),
    }))
    .sort(sortAlphabetically((o: SelectOption<string>) => o.label, SORT_ORDER.ASC));

  const hasOnlyOneOrg = orgOptions.length === 1;

  const jobsToDisplay = filterByOrgId
    ? jobs.filter((job) => job.orgId === parseInt(filterByOrgId, 10))
    : jobs;

  const jobOptions = (jobsToDisplay || [])
    .map((job) => ({
      // when there are multiple orgs, to reduce confusion job label is in the format of
      //   OrgName: JobTitle (Location)
      label: `${hasOnlyOneOrg || filterByOrgId ? '' : `${job.org.name} - `}${formatLocation(
        job,
      )} - ${job.title}`,
      value: job.job_id.toString(),
    }))
    .sort(sortAlphabetically((o: SelectOption<string>) => o.label, SORT_ORDER.ASC));

  const jobLocationOptions = (jobsToDisplay || [])
    .map((job, index, jobsArray) => {
      const matchingJob = jobsArray.find((j) => formatLocation(j) === formatLocation(job));
      if (matchingJob && index !== jobsArray.indexOf(matchingJob)) {
        return {};
      }
      return {
        label: formatLocation(job),
        value: `${job.city?.toUpperCase()}, ${job.state?.toUpperCase()}`,
      };
    })
    .filter((jobOption): jobOption is SelectOption<string> => !!jobOption.label)
    .sort(sortAlphabetically((o: SelectOption<string>) => o.label, SORT_ORDER.ASC));

  const usesPhoneInterview =
    filterByOrgId && orgs.find((o) => o.org_id === parseInt(filterByOrgId, 10))?.usesPhoneInterview;

  const statusOptions = (
    includeRejectedExpired
      ? getAllStatuses(!!usesPhoneInterview)
      : getAllHiringStatuses(!!usesPhoneInterview).concat(SAVED_STATUS)
  ).map((status) => ({
    label: statusDisplay(status),
    value: status,
  }));

  const ApplicantOrgField = ({ className }: { className: string }) =>
    useMemo(() => {
      const orgValue = hasOnlyOneOrg ? orgOptions[0].value : filterByOrgId || defaultFilterByOrgId;

      return (
        <UiFilterSelect
          label="Organization"
          value={orgValue}
          onChange={handleFilterByOrgIdChange}
          options={orgOptions}
          className={className}
          placeholder="All Organizations"
          disabled={fieldsDisabled || hasOnlyOneOrg}
          hideClear={!!hasOnlyOneOrg}
        />
      );
    }, [className]);

  const ApplicantJobField = ({ className }: { className: string }) =>
    useMemo(
      () => (
        <UiFilterSelect
          label="Job"
          value={filterByJobId || defaultFilterByJobId}
          onChange={handleFilterByJobIdChange}
          options={jobOptions}
          className={className}
          placeholder="All Jobs"
          disabled={fieldsDisabled || !jobOptions.length}
        />
      ),
      [className],
    );

  const ApplicantLocationField = ({ className }: { className: string }) =>
    useMemo(
      () => (
        <UiFilterSelect
          label="Location"
          value={filterByLocation || defaultFilterByLocation}
          onChange={handleFilterByLocationChange}
          options={jobLocationOptions}
          className={className}
          placeholder="All Locations"
          disabled={fieldsDisabled || !jobLocationOptions.length}
        />
      ),
      [className],
    );

  const ApplicantStatusField = ({ className }: { className: string }) =>
    useMemo(
      () => (
        <UiFilterSelect
          label="Status"
          value={filterByStatus || defaultFilterByStatus}
          onChange={handleFilterByStatusChange}
          options={statusOptions}
          className={className}
          placeholder="All Statuses"
          disabled={fieldsDisabled}
        />
      ),
      [className],
    );

  const IncludeRejectedExpiredToggle = ({ className }: { className: string }) =>
    useMemo(
      () => (
        <UiFilterToggle
          label={`${includeRejectedExpired ? 'Exclude' : 'Include'} Rejected/Expired`}
          value={includeRejectedExpired || defaultIncludeRejectedExpired}
          onChange={handleIncludeRejectedExpiredChange}
          className={className}
          disabled={fieldsDisabled}
        />
      ),
      [className],
    );

  const searchFieldLabel = 'Name or Phone Number';

  return (
    <div className="card u-margin-top u-margin-bottom">
      {variant === 'small' ? (
        <div className="card-body u-padding-top u-display-flex u-flexDirection-column u-justifyContent-flexEnd">
          <Grid
            container
            spacing={1}
            alignContent="center"
            alignItems="flex-start"
            className="u-width-100"
          >
            <Grid item xs={12} sm={4} md={3}>
              {/* Pulling this element out into a deduplicated component causes it to lose focus after each key press */}
              <UiFilterTextField
                label={searchFieldLabel}
                placeholder={searchFieldLabel}
                value={filterByNameOrPhoneNumber || defaultFilterByName}
                onChange={updateSearchTerm}
                textBoxWidth={filterWidth}
                disabled={fieldsDisabled}
                autoFocus={!!fieldsDisabled}
              />
            </Grid>
            <Grid item xs={12} sm={4} md={3}>
              <ApplicantOrgField className={filterWidth} />
            </Grid>
            <Grid item xs={12} sm={4} md={3}>
              <ApplicantJobField className={filterWidth} />
            </Grid>
            <Grid item xs={12} sm={4} md={3}>
              <ApplicantLocationField className={filterWidth} />
            </Grid>
            <Grid item xs={12} sm={4} md={3}>
              <ApplicantStatusField className={filterWidth} />
            </Grid>
            <Grid item xs={12} sm={4} md={3}>
              <StatusChangeReasonFilter
                changeReasons={statusChangeReasons}
                className={filterWidth}
                value={filterByReason || defaultFilterByReason}
                onChange={handleFilterByReasonChange}
                disabled={!!fieldsDisabled}
                placeholder="All Status Reasons"
              />
            </Grid>
            <Grid item xs={12} sm={4} md={3}>
              <IncludeRejectedExpiredToggle className={buttonWidth ?? filterWidth} />
            </Grid>
          </Grid>
        </div>
      ) : (
        <div className="card-body u-display-flex u-justifyContent-spaceBetween u-alignItems-flexStart">
          <div className="u-display-flex u-alignItems-flexStart">
            <UiFilterTextField
              label={searchFieldLabel}
              placeholder={searchFieldLabel}
              value={filterByNameOrPhoneNumber || defaultFilterByName}
              onChange={updateSearchTerm}
              textBoxWidth={filterWidth}
              disabled={fieldsDisabled}
              autoFocus={!!fieldsDisabled}
            />
            <ApplicantOrgField className={`u-margin-left ${filterWidth}`} />
            <ApplicantJobField className={`u-margin-left ${filterWidth}`} />
            <ApplicantLocationField className={`u-margin-left ${filterWidth}`} />
            <ApplicantStatusField className={`u-margin-left ${filterWidth}`} />
            <StatusChangeReasonFilter
              changeReasons={statusChangeReasons}
              className={`u-margin-left ${filterWidth}`}
              value={filterByReason || defaultFilterByReason}
              onChange={handleFilterByReasonChange}
              disabled={!!fieldsDisabled}
              placeholder="All Status Reasons"
            />
            <IncludeRejectedExpiredToggle
              className={`u-margin-left ${buttonWidth ?? filterWidth}`}
            />
          </div>
        </div>
      )}
    </div>
  );
};
